/* eslint-disable no-unused-vars */
import react, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';

import NewBookingTab from "../Common/NewBookingTab";
import BookParkingOrDesk from "../Common/BookParkingOrDesk";
import BookMeeting from "../Common/BookMeeting";
import StepProgress from "../Common/StepProgress";
import Sidebar from "../Sidebar/Sidebar";
import { Images } from "../Common/Images";
import NotificationPanel from "../Common/Notification";
import { QuickBookingInfo_api } from "./NewBookingAPI";
import CreditInfoCard from "../Common/CreditInfoCard";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";

const NewBookingUI = () => {
    const isTabEnabled = (value) => Number(value) === 1;
    const getFirstAvailableTab = (data) => {
        const priorityTabMap = {
            1: "parking",
            2: "desking",
            3: "meeting"
        };
        const priorityTab = priorityTabMap[data?.PriorityTab];
        // SHOW/ HIDE TABS BASED ON API RESPONSE AND PRIORITY
        if (
            (priorityTab === "parking" && isTabEnabled(data?.ParkingTabActive)) ||
            (priorityTab === "desking" && isTabEnabled(data?.DeskingTabActive)) ||
            (priorityTab === "meeting" && isTabEnabled(data?.MeetingRoomTabActive))
        ) {
            return priorityTab;
        }

        if (isTabEnabled(data?.ParkingTabActive)) return "parking";
        if (isTabEnabled(data?.DeskingTabActive)) return "desking";
        if (isTabEnabled(data?.MeetingRoomTabActive)) return "meeting";
        return "parking";
    };

    const getFirstName = () => sessionStorage.getItem("firstname");
    const { t } = useTranslation("common");
    const { i18n } = useTranslation();
    const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
    const locale = i18n.language.split("-")[0];
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [activeTab, setActiveTab] = useState("parking");
    const [step, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [bookingInfo, setBookingInfo] = useState(null);

    const [needApproval, setNeedApproval] = useState(false);
    const [meetingStep, setMeetingStep] = useState(0);

    const fetchQuickBookingInfo = async () => {

        try {

            setIsLoading(true);

            const response = await QuickBookingInfo_api({
                GuId: sessionStorage.getItem("GuId")
            });
            handleApiUiResponse(response, {
                successToaster: 0,
                errorToaster: 1,
                alertPopup: 1
            });
            const data = response?.Data?.Records;

            if (data) {

                setBookingInfo(data);
                setActiveTab(getFirstAvailableTab(data));

            }

        } catch (error) {

            console.error("QuickBookingInfo error", error);

        } finally {

            setIsLoading(false);

        }

    };

    useEffect(() => {
        document.title = `${t("react_new_booking")} | Ronspot`
    }, []);

    useEffect(() => {
        fetchQuickBookingInfo();
    }, []);

    const hasParkingZones = isTabEnabled(bookingInfo?.ParkingTabActive);
    const hasDeskingZones = isTabEnabled(bookingInfo?.DeskingTabActive);
    const hasMeetingZones = isTabEnabled(bookingInfo?.MeetingRoomTabActive);

    useEffect(() => {
        const availableTabs = [];

        if (hasParkingZones) availableTabs.push("parking");
        if (hasDeskingZones) availableTabs.push("desking");
        if (hasMeetingZones) availableTabs.push("meeting");

        if (availableTabs.length === 0) {
            return;
        }

        if (!availableTabs.includes(activeTab)) {
            setActiveTab(availableTabs[0]);
            setStep(0);
        }
    }, [activeTab, hasParkingZones, hasDeskingZones, hasMeetingZones]);

    const isBookingTab = ["parking", "desking"].includes(activeTab);

    let title = t("react_new_meeting");
    const isApprovalTitle = needApproval && meetingStep === 4 && activeTab === "meeting";

    if (step === 1) {
        title = t("react_select_spot");
    }
    else if (step === 0 && isBookingTab) {
        title = t("react_new_booking");
    }
    else if (needApproval && meetingStep === 4 && activeTab === "meeting") {
        title = t("react_booking_requires_approval");
    }
    else if (step === 2 && activeTab === "meeting") {
        title = t("react_add_participants");
    }
    else if (step === 2 && isBookingTab) {
        title = t("react_confirm_your_booking");
    }
    else if (meetingStep === 3 && activeTab === "meeting") {
        title = t("confirmation");
    }
    const [zoneCreditValue, setZoneCreditValue] = useState(0);
    const shouldShowCreditInfo = zoneCreditValue > 0;
    const headerTitle = t("react_hi").replace("%s", getFirstName());

    return (
        <div className="content-area new-booking-page">
            {renderApiUi(isLoading)}

            {/* <Header /> */}
            <header className="header">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <div className="greeting-title">
                                <p>{headerTitle}</p>
                            </div>
                        </div>
                        <div className="col-md-6"></div>
                        <div className="col-md-3">
                            <div className="menu-bar-options d-flex justify-content-end gap-3">
                                {shouldShowCreditInfo && (
                                    <CreditInfoCard
                                        userZoneCreditValue={zoneCreditValue}
                                        userZoneCreditBalance={bookingInfo?.CreditBalance}
                                        creditBalanceRefillEvery={bookingInfo?.CreditBalanceRefillEvery}
                                        IsThumbnailPresent={bookingInfo?.IsThumbnailPresent}
                                        CompanyId={bookingInfo?.CompanyId}
                                    />)}
                                <div className="notification-info">
                                    <a href="#" data-bs-toggle="offcanvas" data-bs-target="#notification-panel" aria-controls="notification-panel" className="position-relative notification-icon p-0">
                                        <img src={Images.notification} alt="" />{hasUnreadNotifications && (
                                            <span className="orange-dot"></span>
                                        )}</a>
                                </div>
                                <NotificationPanel onUnreadChange={setHasUnreadNotifications} />
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            {isLoading && (
                <div id="preloader">
                    <img src={Images.LoaderFull} alt="" />
                </div>
            )}

            <Sidebar
                IsThumbnailPresent={bookingInfo?.IsThumbnailPresent}
                CompanyId={bookingInfo?.CompanyId}
            />


            <div className="pt-96 pb-96 my-container active-cont me-3">
                <div className="container mw-100">
                    <div className="new-booking-tab-wrap d-flex pt-30">

                        <h2
                            className={`module-title w-25 ${isApprovalTitle ? "text-red" : ""
                                }`}
                        >
                            {title}
                        </h2>

                        <div className="new-booking-tab-inner">

                            {step === 0 && (
                                <NewBookingTab
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    setStep={setStep}
                                    tabAvailability={{
                                        parking: hasParkingZones,
                                        desking: hasDeskingZones,
                                        meeting: hasMeetingZones
                                    }}
                                />
                            )}

                            {/* Tab Content */}

                            {/* Tab Content */}
                            {activeTab === "parking" && (<BookParkingOrDesk
                                selectedTab={1}
                                step={step}
                                setStep={setStep}
                                bookingInfo={activeTab === "parking" ? bookingInfo?.Parking : bookingInfo?.Desking}
                                setZoneCreditValue={setZoneCreditValue}
                            />)}

                            {activeTab === "desking" && (<BookParkingOrDesk
                                selectedTab={2}
                                step={step}
                                setStep={setStep}
                                bookingInfo={activeTab === "parking" ? bookingInfo?.Parking : bookingInfo?.Desking}
                                setZoneCreditValue={setZoneCreditValue}
                            />)}

                            {activeTab === "meeting" && (<BookMeeting
                                selectedTab={3}
                                step={step}
                                setStep={setStep}
                                bookingInfo={bookingInfo?.MeetingRoom}
                                needApproval={needApproval}
                                setNeedApproval={setNeedApproval}
                                setMeetingStep={setMeetingStep}
                                setZoneCreditValue={setZoneCreditValue}
                            />)}

                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewBookingUI;
